import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Smartphone, Download, Share, PlusSquare, Apple, CheckCircle2 } from "lucide-react";

// Set this once you've published the Android build (Play Store URL or hosted APK).
const ANDROID_APK_URL = process.env.REACT_APP_ANDROID_APK_URL || "";

export default function GetApp() {
  const [canInstall, setCanInstall] = useState(!!window.deferredPrompt);
  const [installed, setInstalled] = useState(false);
  const url = typeof window !== "undefined" ? window.location.origin : "https://safehavensafetyplan.org";

  useEffect(() => {
    const onAvail = () => setCanInstall(true);
    const onDone = () => { setInstalled(true); setCanInstall(false); };
    window.addEventListener("pwa-installable", onAvail);
    window.addEventListener("pwa-installed", onDone);
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) setInstalled(true);
    return () => { window.removeEventListener("pwa-installable", onAvail); window.removeEventListener("pwa-installed", onDone); };
  }, []);

  const install = async () => {
    const dp = window.deferredPrompt;
    if (!dp) { toast.info("On this device, use your browser menu → 'Add to Home Screen'."); return; }
    dp.prompt();
    const { outcome } = await dp.userChoice;
    if (outcome === "accepted") toast.success("Installing SafeHaven…");
    window.deferredPrompt = null;
    setCanInstall(false);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 fade-up" data-testid="get-app-page">
      <header className="text-center">
        <div className="w-14 h-14 rounded-2xl bg-teal-700 text-white flex items-center justify-center mx-auto mb-3"><Smartphone className="w-7 h-7" /></div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Get SafeHaven on your phone</h1>
        <p className="text-slate-600 mt-2">Install it for one-tap access to your safety plan, crisis contacts and AI companion — even offline.</p>
      </header>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Install / Add to home screen */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="get-app-install-card">
          <h2 className="font-display text-lg font-bold text-slate-900 mb-1">Install the app</h2>
          <p className="text-sm text-slate-600 mb-4">Works on Android &amp; desktop instantly — no app store needed.</p>
          {installed ? (
            <div className="flex items-center gap-2 text-teal-700 font-semibold"><CheckCircle2 className="w-5 h-5" /> Installed on this device</div>
          ) : (
            <button data-testid="get-app-install-btn" onClick={install} className="w-full bg-teal-700 hover:bg-teal-800 text-white rounded-xl py-3 font-semibold flex items-center justify-center gap-2 transition-colors">
              <Download className="w-4 h-4" /> {canInstall ? "Install SafeHaven" : "Add to Home Screen"}
            </button>
          )}
          <div className="mt-5 space-y-3 text-sm text-slate-600">
            <div className="flex items-start gap-2"><Apple className="w-4 h-4 mt-0.5 shrink-0" /><span><b>iPhone/iPad (Safari):</b> tap <Share className="inline w-3.5 h-3.5" /> Share → <PlusSquare className="inline w-3.5 h-3.5" /> <b>Add to Home Screen</b>.</span></div>
            <div className="flex items-start gap-2"><Smartphone className="w-4 h-4 mt-0.5 shrink-0" /><span><b>Android (Chrome):</b> tap <b>Install</b> above, or use the ⋮ menu → <b>Install app</b>.</span></div>
          </div>
        </div>

        {/* QR to open on phone */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex flex-col items-center justify-center text-center" data-testid="get-app-qr-card">
          <h2 className="font-display text-lg font-bold text-slate-900 mb-3">Scan to open on your phone</h2>
          <div className="bg-white p-3 rounded-xl border border-slate-200">
            <QRCodeSVG value={url} size={168} fgColor="#0f766e" level="M" />
          </div>
          <p className="text-xs text-slate-500 mt-3 break-all">{url}</p>
        </div>
      </div>

      {/* Android native app */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="get-app-android-card">
        <h2 className="font-display text-lg font-bold text-slate-900 mb-1">Android app (.apk)</h2>
        {ANDROID_APK_URL ? (
          <>
            <p className="text-sm text-slate-600 mb-4">Download and install the native Android app.</p>
            <a data-testid="get-app-apk-link" href={ANDROID_APK_URL} className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl px-5 py-3 font-semibold">
              <Download className="w-4 h-4" /> Download for Android
            </a>
          </>
        ) : (
          <p className="text-sm text-slate-600">The native Android app is coming soon. In the meantime, use <b>Install / Add to Home Screen</b> above — it behaves just like a native app.</p>
        )}
      </div>
    </div>
  );
}
